import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import { VITAL_SIGNS, type VitalKey } from '@wodi/domain';
import type { ClinicalAlertRule, ClinicalRulesView, TriageView } from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Kbd,
  Notice,
  RadioGroup,
  TextArea,
  useShortcut,
  VitalField,
} from '@wodi/ui';
import { useMemo, useRef, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { sessionQuery } from '../api/session.ts';
import { recordTriage } from '../api/triage.ts';
import { useOnline } from '../hooks/use-online.ts';
import { useFlushQueue, useQueuedAction } from '../offline/offline-status.tsx';
import { queueTriage, QueuedTriageNotice, RefusedTriageNotice } from '../offline/queued-triage.tsx';
import { retryQueue } from '../offline/retry-queue.ts';
import { useDeviceDraft } from '../offline/use-device-draft.ts';
import { triageDraftKey } from '../patients/drafts.ts';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import {
  ALERT_FALLBACK,
  bloodPressureError,
  domainRules,
  FORM_VITALS,
  judgeField,
  liveBmi,
  muacShown,
  pendingAlerts,
  vitalInputs,
  type PendingAlert,
  type VitalTexts,
} from './triage-model.ts';
import styles from './triage.module.css';

// The triage form (docs/plans/phase1-triage.md §6.3; DESIGN.md §8 Triage): vitals in the
// paper-chart order, complaint, category, then save. Values are flagged as they are typed
// and checked again by the server, which is the authority. Interruptive alerts appear
// above the save button and need a reason each. A draft is kept on this device.

interface Draft {
  texts: VitalTexts;
  glucoseContext: string | null;
  complaint: string;
  category: string | null;
  showMuac: boolean;
}

const EMPTY: Draft = {
  texts: {},
  glucoseContext: null,
  complaint: '',
  category: null,
  showMuac: false,
};

const categoryId = (c: { system: string; code: string }) => `${c.system}|${c.code}`;

function VitalInput({
  vital,
  value,
  onChange,
  rules,
  ageDays,
  context,
  extraError,
  autoFocus = false,
}: {
  vital: VitalKey;
  value: string;
  onChange: (value: string) => void;
  rules: ReturnType<typeof domainRules>;
  ageDays: number | null;
  context: string | null;
  extraError?: string | null;
  autoFocus?: boolean;
}) {
  const definition = VITAL_SIGNS[vital];
  const judged = judgeField(vital, value, rules, ageDays, context);
  const error = judged.error ?? extraError ?? undefined;
  return (
    <VitalField
      label={definition.label}
      unit={definition.unitDisplay}
      value={value}
      onChange={onChange}
      autoFocus={autoFocus}
      provisional={judged.provisional}
      {...(judged.flag === undefined ? {} : { flag: judged.flag })}
      {...(error === undefined ? {} : { errorMessage: error })}
    />
  );
}

/** When this device can't keep a triage (storage blocked, e.g. a private window). */
const KEEP_FAILED =
  "This device can't keep the triage (its storage is blocked). Your entries are still here. Save triage again when the connection is back.";

export function TriageForm({
  view,
  rules: rulesView,
  onSaved,
  onCancel,
}: {
  view: TriageView;
  rules: ClinicalRulesView;
  onSaved: (view: TriageView) => void;
  /** Present when re-triaging: go back to the current assessment. */
  onCancel?: () => void;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const draftKey =
    session === null ? null : triageDraftKey(session.user.id, session.facility.id, view.visitId);
  const {
    value: draft,
    setValue: setDraft,
    available: draftAvailable,
    clear: clearDraft,
  } = useDeviceDraft<Draft>(draftKey, EMPTY);
  const [reasons, setReasons] = useState<Partial<Record<ClinicalAlertRule, string>>>({});
  // Saved on this device while offline (ADR 0018): waiting, or refused by Wodi.
  const queued = useQueuedAction('triage', view.visitId);
  const location = useLocation();
  const flushQueue = useFlushQueue();
  const [serverAlerts, setServerAlerts] = useState<ClinicalAlertRule[]>(
    () => (queued?.error?.ruleIds ?? []) as ClinicalAlertRule[],
  );
  const [tried, setTried] = useState(false);
  const [keepError, setKeepError] = useState<string | null>(null);
  const requestId = useRef(crypto.randomUUID());

  const rules = useMemo(() => domainRules(rulesView.rules), [rulesView.rules]);
  const ageDays = view.patientAgeDays;
  const category = rulesView.categories.find((c) => categoryId(c) === draft.category);
  const bpError = bloodPressureError(draft.texts);
  const fieldErrors = FORM_VITALS.some(
    (key) =>
      judgeField(
        key,
        draft.texts[key] ?? '',
        rules,
        ageDays,
        key === 'blood_glucose' ? draft.glucoseContext : null,
      ).error !== undefined,
  );

  const foreseen = pendingAlerts({
    texts: draft.texts,
    glucoseContext: draft.glucoseContext,
    rules,
    ageDays,
    settings: rulesView.settings,
    allergies: view.allergies,
    category,
  });
  const alerts: PendingAlert[] = [
    ...foreseen,
    ...serverAlerts
      .filter((id) => !foreseen.some((a) => a.ruleId === id))
      .map((ruleId) => ({ ruleId, ...ALERT_FALLBACK[ruleId] })),
  ];
  const reasonOf = (id: ClinicalAlertRule) => reasons[id]?.trim() ?? '';
  const unacknowledged = alerts.filter((a) => reasonOf(a.ruleId).length < REASON_MIN_LENGTH);

  const set = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };
  const setText = (key: VitalKey) => (value: string) => {
    setDraft((d) => ({ ...d, texts: { ...d.texts, [key]: value } }));
  };

  const triageRequest = () => {
    if (category === undefined) throw new Error('no category');
    return {
      clientRequestId: requestId.current,
      vitals: vitalInputs(draft.texts, draft.glucoseContext),
      chiefComplaint: draft.complaint.trim(),
      category: { system: category.system, code: category.code },
      acknowledgements: alerts.map((a) => ({ ruleId: a.ruleId, reason: reasonOf(a.ruleId) })),
    };
  };
  // Offline, or the connection dropped mid-save: keep it on this device with the same
  // request ID, so a save that did reach Wodi is not recorded twice.
  const keepOnDevice = async () => {
    setKeepError(null);
    try {
      await queueTriage({
        visitId: view.visitId,
        patientId: view.patientId,
        request: triageRequest(),
        href: location.href,
        draftKey,
        urgent: category?.requiresImmediateAttention === true,
      });
    } catch {
      setKeepError(KEEP_FAILED);
      return;
    }
    requestId.current = crypto.randomUUID();
    if (navigator.onLine) flushQueue();
  };

  const save = useMutation({
    mutationFn: () => recordTriage(view.visitId, triageRequest()),
    onSuccess: async (saved) => {
      await clearDraft();
      queryClient.setQueryData(['visits', view.visitId, 'triage'], saved);
      await queryClient.invalidateQueries({ queryKey: ['queues'] });
      await queryClient.invalidateQueries({ queryKey: ['patients', view.patientId] });
      requestId.current = crypto.randomUUID();
      onSaved(saved);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'offline') {
        void keepOnDevice();
        return;
      }
      if (error instanceof ApiError && error.code === 'alert_acknowledgement_required') {
        const ids = error.context?.['ruleIds'];
        if (Array.isArray(ids)) setServerAlerts(ids as ClinicalAlertRule[]);
      }
      // A retry after an error is a new attempt, not a replay of a stored one.
      requestId.current = crypto.randomUUID();
    },
  });

  const complaintMissing = draft.complaint.trim() === '';
  const submit = () => {
    setTried(true);
    if (
      complaintMissing ||
      category === undefined ||
      bpError !== null ||
      fieldErrors ||
      unacknowledged.length > 0 ||
      save.isPending
    ) {
      return;
    }
    void (async () => {
      if (queued !== undefined) await retryQueue.remove(queued.id);
      if (online) save.mutate();
      else await keepOnDevice();
    })();
  };
  useShortcut('mod+enter', submit, { allowInFields: true, description: 'Save triage' });

  const showMuac = draft.showMuac || muacShown(rulesView.settings, ageDays);
  const bmi = liveBmi(draft.texts);
  const vital = (key: VitalKey, extra?: string | null, autoFocus = false) => (
    <VitalInput
      vital={key}
      autoFocus={autoFocus}
      value={draft.texts[key] ?? ''}
      onChange={setText(key)}
      rules={rules}
      ageDays={ageDays}
      context={key === 'blood_glucose' ? draft.glucoseContext : null}
      {...(extra === undefined ? {} : { extraError: extra })}
    />
  );

  const saveError =
    save.error === null
      ? null
      : save.error instanceof ApiError
        ? save.error.code === 'alert_acknowledgement_required'
          ? null
          : save.error.message
        : 'Triage could not be saved. Try again.';

  if (queued?.status === 'waiting') return <QueuedTriageNotice item={queued} />;

  return (
    <form
      className={styles.form}
      aria-label="Triage"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Vitals</legend>
        <div className={styles.vitalsGrid}>
          {/* The nurse types the weight next: the first field of the paper chart. */}
          {vital('body_weight', undefined, true)}
          {vital('body_height')}
          {vital('body_temperature')}
          {vital('heart_rate')}
          {vital('bp_systolic', bpError)}
          {vital('bp_diastolic')}
          {vital('respiratory_rate')}
          {vital('oxygen_saturation')}
          {vital('pain_score')}
          {showMuac ? vital('muac') : null}
          {vital('blood_glucose')}
        </div>
        {(draft.texts.blood_glucose ?? '').trim() === '' ? null : (
          <RadioGroup
            label="Glucose taken"
            orientation="horizontal"
            value={draft.glucoseContext}
            onChange={(value) => {
              set({ glucoseContext: value });
            }}
            options={[
              { id: 'random', label: 'Random' },
              { id: 'fasting', label: 'Fasting' },
            ]}
          />
        )}
        {showMuac ? null : (
          <div>
            <Button
              variant="quiet"
              dense
              onPress={() => {
                set({ showMuac: true });
              }}
            >
              Add MUAC
            </Button>
          </div>
        )}
        <p className={styles.help} role="status">
          {bmi === null
            ? 'Leave a field empty if it was not taken. BMI is worked out from weight and height.'
            : `BMI ${bmi}`}
        </p>
      </fieldset>

      <TextArea
        label="Chief complaint"
        rows={3}
        value={draft.complaint}
        onChange={(value) => {
          set({ complaint: value });
        }}
        {...(tried && complaintMissing ? { errorMessage: 'Enter the main complaint.' } : {})}
      />

      <RadioGroup
        label="Triage category"
        orientation="horizontal"
        value={draft.category}
        onChange={(value) => {
          set({ category: value });
        }}
        options={rulesView.categories.map((c) => ({
          id: categoryId(c),
          label: c.provisional ? `${c.display} (provisional)` : c.display,
          ...(c.requiresImmediateAttention ? { description: 'Needs a clinician now' } : {}),
        }))}
        {...(tried && category === undefined ? { errorMessage: 'Choose a triage category.' } : {})}
      />
      {rulesView.categories.some((c) => c.provisional) ? (
        <p className={styles.help}>
          Provisional: the category scale has not been approved by a clinical lead yet.
        </p>
      ) : null}

      {alerts.map((alert) => (
        <AlertPanel
          key={alert.ruleId}
          title={alert.title}
          danger={alert.danger}
          reason={reasons[alert.ruleId] ?? ''}
          onReasonChange={(value) => {
            setReasons((r) => ({ ...r, [alert.ruleId]: value }));
          }}
          {...(tried && reasonOf(alert.ruleId).length < REASON_MIN_LENGTH
            ? {
                reasonError: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.`,
              }
            : {})}
        >
          {alert.body}
        </AlertPanel>
      ))}

      {queued?.status === 'failed' ? <RefusedTriageNotice item={queued} /> : null}
      {saveError === null || keepError !== null ? null : <Notice tone="error">{saveError}</Notice>}
      {keepError === null ? null : <Notice tone="error">{keepError}</Notice>}
      {online ? null : (
        <Notice tone="info">
          Offline. Save triage keeps it on this device and sends it to Wodi when the connection is
          back.
        </Notice>
      )}

      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={save.isPending} pendingLabel="Saving…">
          Save triage
        </Button>
        <Kbd combo="mod+enter" />
        {onCancel === undefined ? null : (
          <Button variant="quiet" onPress={onCancel}>
            Keep the current triage
          </Button>
        )}
        {draftAvailable ? (
          <span className={styles.draftStatus} role="status">
            Saved on this device
          </span>
        ) : null}
      </div>
    </form>
  );
}
